import React from "react";
import axios, { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  ComparisonEnvironment,
  ShadowTestResultResponse,
  Stage,
} from "../lusca-search-api/generated-src";
import { ShadowTestApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

interface UseShadowTestResultParams {
  stage: Stage;
  transactionId: string | undefined;
}

// A triggered test writes its result asynchronously; poll until a result newer
// than the one shown at trigger time arrives, or give up after the timeout.
export const SHADOW_TEST_POLL_INTERVAL_MS = 10_000;
export const SHADOW_TEST_POLL_TIMEOUT_MS = 10 * 60_000;

interface PendingResult {
  comparisonEnvironment: ComparisonEnvironment;
  previousTestTimestamp?: number;
}

const responseMessage = (data: unknown) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const body = data as { error?: unknown; message?: unknown };
  if (typeof body.message === "string") {
    return body.message;
  }
  return typeof body.error === "string" ? body.error : undefined;
};

const isMissingResult = (
  error: unknown,
  transactionId: string,
  comparisonEnvironment?: ComparisonEnvironment
) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const expectedMessage =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? `No BETA shadow test result found for: ${transactionId}`
      : `No shadow test result found for: ${transactionId}`;
  return responseMessage(error.response.data) === expectedMessage;
};

export default function useShadowTestResult({
  stage,
  transactionId,
}: UseShadowTestResultParams) {
  const shadowTestApi = ShadowTestApiFactory();
  const [pendingResult, setPendingResult] =
    React.useState<PendingResult | null>(null);

  React.useEffect(() => {
    setPendingResult(null);
  }, [stage, transactionId]);

  const resultQuery = (comparisonEnvironment?: ComparisonEnvironment) => ({
    queryKey: [
      "shadowTestResult",
      stage,
      transactionId,
      comparisonEnvironment ?? ComparisonEnvironment.Gamma,
    ],
    queryFn: async () => {
      if (!transactionId) {
        throw new Error("Transaction ID is required");
      }
      try {
        const { data }: AxiosResponse<ShadowTestResultResponse> =
          await shadowTestApi.getShadowTestResult(
            stage,
            transactionId,
            comparisonEnvironment
          );
        return data;
      } catch (error) {
        if (isMissingResult(error, transactionId, comparisonEnvironment)) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!transactionId && !!stage,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval:
      pendingResult?.comparisonEnvironment ===
      (comparisonEnvironment ?? ComparisonEnvironment.Gamma)
        ? SHADOW_TEST_POLL_INTERVAL_MS
        : (false as const),
    refetchIntervalInBackground: true,
  });

  const gammaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery()
  );
  const betaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery(ComparisonEnvironment.Beta)
  );

  const pendingData =
    pendingResult?.comparisonEnvironment === ComparisonEnvironment.Beta
      ? betaQuery.data
      : gammaQuery.data;
  React.useEffect(() => {
    if (!pendingResult || !pendingData) return;
    if (
      pendingResult.previousTestTimestamp === undefined ||
      pendingData.testTimestamp > pendingResult.previousTestTimestamp
    ) {
      setPendingResult(null);
    }
  }, [pendingData, pendingResult]);

  React.useEffect(() => {
    if (!pendingResult) return undefined;
    const timeout = setTimeout(
      () => setPendingResult(null),
      SHADOW_TEST_POLL_TIMEOUT_MS
    );
    return () => clearTimeout(timeout);
  }, [pendingResult]);

  const awaitNewResult = React.useCallback(
    (comparisonEnvironment: ComparisonEnvironment) => {
      const query =
        comparisonEnvironment === ComparisonEnvironment.Beta
          ? betaQuery
          : gammaQuery;
      setPendingResult({
        comparisonEnvironment,
        previousTestTimestamp: query.data?.testTimestamp,
      });
      query.refetch().catch(() => undefined);
    },
    [betaQuery, gammaQuery]
  );

  const refetch = async () => {
    await Promise.all([gammaQuery.refetch(), betaQuery.refetch()]);
  };

  return {
    gammaResult: gammaQuery.data,
    betaResult: betaQuery.data,
    gammaIsLoading: gammaQuery.isLoading,
    betaIsLoading: betaQuery.isLoading,
    gammaIsFetching: gammaQuery.isFetching,
    betaIsFetching: betaQuery.isFetching,
    gammaError: gammaQuery.error || null,
    betaError: betaQuery.error || null,
    refetch,
    awaitNewResult,
    awaitingEnvironment: pendingResult?.comparisonEnvironment,
  };
}
